import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Loader2, Save, ChevronDown, ChevronRight } from "lucide-react";
import { listNoticeTemplates, createNoticeTemplate, updateNoticeTemplate, deleteNoticeTemplate } from "@/lib/api";

// Portal Settings → UK → Notice Templates. CRUD for the "Issue document"
// templates (PDN / DN / Redemption / Settlement…) used in case-file emails.
export const NoticeTemplatesSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newTpl, setNewTpl] = useState({ name: "", subject: "", body: "" });

  const load = async () => {
    try {
      const r = await listNoticeTemplates();
      setTemplates(r.templates || []);
      setPlaceholders(r.placeholders || []);
    } catch (e) {
      toast.error("Failed to load notice templates: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const draftOf = (t) => drafts[t.template_id] || { name: t.name, subject: t.subject, body: t.body };
  const setDraft = (id, patch) => setDrafts((prev) => {
    const t = templates.find((x) => x.template_id === id) || {};
    const base = prev[id] || { name: t.name, subject: t.subject, body: t.body };
    return { ...prev, [id]: { ...base, ...patch } };
  });

  const save = async (t) => {
    const d = draftOf(t);
    setSaving(t.template_id);
    try {
      await updateNoticeTemplate(t.template_id, { name: d.name, subject: d.subject, body: d.body });
      toast.success(`"${d.name}" saved`);
      setDrafts((prev) => { const n = { ...prev }; delete n[t.template_id]; return n; });
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(null); }
  };

  const toggleActive = async (t) => {
    try {
      await updateNoticeTemplate(t.template_id, { active: !t.active });
      setTemplates((prev) => prev.map((x) => x.template_id === t.template_id ? { ...x, active: !t.active } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" template? This cannot be undone.`)) return;
    try {
      await deleteNoticeTemplate(t.template_id);
      toast.success(`"${t.name}" deleted`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const addNew = async () => {
    if (!newTpl.name.trim() || !newTpl.subject.trim() || !newTpl.body.trim()) {
      toast.error("Name, subject and body are all required."); return;
    }
    setSaving("new");
    try {
      await createNoticeTemplate(newTpl);
      toast.success(`"${newTpl.name}" created`);
      setNewTpl({ name: "", subject: "", body: "" });
      setAdding(false);
      await load();
    } catch (e) {
      toast.error("Create failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(null); }
  };

  return (
    <Card data-testid="notice-templates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-4 h-4 text-rose-600" /> Notice Templates
        </CardTitle>
        <CardDescription className="text-xs">
          Email templates for the "Issue document" button on Legal &amp; Collections case files
          (PDN / DN / Redemption / Settlement etc). Placeholders fill in from the case at send time;
          {" {expiry_date} "} comes from the expiry set in the issue dialog.
        </CardDescription>
        <div className="flex flex-wrap gap-1 pt-1">
          {placeholders.map((p) => (
            <Badge key={p.key} variant="outline" className="text-[10px] font-mono" title={`${p.label} — e.g. ${p.example}`}>
              {p.key}
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="text-xs text-muted-foreground py-4 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>
        ) : (
          <>
            {templates.map((t) => {
              const open = openId === t.template_id;
              const d = draftOf(t);
              const dirty = d.name !== t.name || d.subject !== t.subject || d.body !== t.body;
              return (
                <div key={t.template_id} className="border rounded-md" data-testid={`notice-template-${t.template_id}`}>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button onClick={() => setOpenId(open ? null : t.template_id)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`notice-template-toggle-${t.template_id}`}>
                      {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                      <span className="text-sm font-medium truncate">{t.name}</span>
                      <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">{t.subject}</span>
                    </button>
                    <Switch checked={!!t.active} onCheckedChange={() => toggleActive(t)} data-testid={`notice-template-active-${t.template_id}`} />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" onClick={() => remove(t)} data-testid={`notice-template-delete-${t.template_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {open && (
                    <div className="px-3 pb-3 space-y-2 border-t pt-2">
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Name</label>
                          <Input value={d.name} onChange={(e) => setDraft(t.template_id, { name: e.target.value })} className="h-8 text-xs" data-testid={`notice-template-name-${t.template_id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Subject</label>
                          <Input value={d.subject} onChange={(e) => setDraft(t.template_id, { subject: e.target.value })} className="h-8 text-xs" data-testid={`notice-template-subject-${t.template_id}`} />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Body</label>
                        <Textarea value={d.body} onChange={(e) => setDraft(t.template_id, { body: e.target.value })} rows={10} className="text-xs font-mono" data-testid={`notice-template-body-${t.template_id}`} />
                      </div>
                      <div className="flex justify-end">
                        <Button size="sm" onClick={() => save(t)} disabled={!dirty || saving === t.template_id} data-testid={`notice-template-save-${t.template_id}`}>
                          {saving === t.template_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                          Save changes
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {adding ? (
              <div className="border-2 border-dashed rounded-md p-3 space-y-2" data-testid="notice-template-new-form">
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={newTpl.name} onChange={(e) => setNewTpl((p) => ({ ...p, name: e.target.value }))} placeholder="Template name (e.g. Default Notice (DN))" className="h-8 text-xs" data-testid="notice-template-new-name" />
                  <Input value={newTpl.subject} onChange={(e) => setNewTpl((p) => ({ ...p, subject: e.target.value }))} placeholder="Email subject (e.g. Default Notice — {sf_number})" className="h-8 text-xs" data-testid="notice-template-new-subject" />
                </div>
                <Textarea value={newTpl.body} onChange={(e) => setNewTpl((p) => ({ ...p, body: e.target.value }))} rows={8} placeholder="Email body — use {first_name}, {expiry_date}, {sf_number} etc" className="text-xs font-mono" data-testid="notice-template-new-body" />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
                  <Button size="sm" onClick={addNew} disabled={saving === "new"} data-testid="notice-template-create-btn">
                    {saving === "new" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                    Create template
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-notice-template-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add template
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NoticeTemplatesSettings;
